import { z } from 'zod';
import { zBasicViewItem, NestedColumnsBasicViewItem, NestedOptionsBasicViewItem, NestedViewBasicViewItem, zCompanionView, ConfigRefs, zConfigurableGridActions, zConfigurableGridGraph, MainView, MassEditConfig, SalesAdjustment, SearchIndexes, SubheaderDropdown, ParetoSection, StyleEditHeader, StyleEditSection, TimeLevels, MainViewDefnConfig, GridMainViewDefn, zCompanionViewDefn, zConfigurablePostAction, zTopAttribute, ConfigureViewDefn, zTransposedColumns, zMassEditCoordinateMap, zNoRowsOverlay, zCompanionCard, zConfigurableGridLinkButton, } from './general';
import { zAdornments } from './literals';
import { zClientDataApi, zHasTitle, zIdentifierProps, zDataApi, zListDataApi } from '../confdefnView';
import { zWorklistTab } from '../Worklist';
// Adding to this validator type will allow certain features to be configured for all views.
// This just means the component will support adornments, it will still need to be configured to render in the card/grid items
export const zHasAdornments = z.object({
    adornments: z.array(zAdornments).default([]),
});
const zhasCardWidth = z.object({
    numberCardsWide: z.number().optional().default(2),
});
const zChartTooltipConfig = z.object({
    dataIndex: z.string(),
    renderer: z.string(),
    text: z.string(),
});
export const zRefreshOn = z.enum(['all', 'none', 'planCompleteOnly']);
const zConfigurableGridTopAttributes = z
    .object({
    title: z.string().optional(),
    expanded: z.boolean().optional(),
    /** DataIndices for the default selections for the attributes configured in the `right` array */
    defaults: z.array(z.string()).optional().default([]),
    left: z.array(zTopAttribute).default([]),
    right: z.array(zTopAttribute).default([]),
    refreshOn: zRefreshOn.default(zRefreshOn.enum.none),
})
    .optional();
export const zSubheaderDropdowns = z.object({
    groupBy: SubheaderDropdown.optional(),
    topMember: z
        .object({
        label: z.string(),
        dataApi: zDataApi,
    })
        .optional(),
});
export const zConfigurableGridViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { title: z.string(), 
    // TODO: can this shape be converted to match zCompanionView
    // TODO: update name in viewdefn to companionList,
    // it's configured as CompanionGrid but really represents the companion list's config
    companionGrid: z.object({
        main: zCompanionCard.optional(),
        label: z.string(),
        sortConfig: z.object({
            defaults: z.string().optional(),
            view: z.array(zBasicViewItem),
        }),
    }), subheaderDropdowns: zSubheaderDropdowns, 
    // pass through column config because it has too many property
    columns: z.array(zBasicViewItem), main: GridMainViewDefn.optional(), actions: zConfigurableGridActions.optional(), $configRefs: ConfigRefs.optional(), salesAdjustment: SalesAdjustment, showPublish: z.boolean().optional(), hideUnpublish: z.boolean().optional(), publishText: z.string().optional(), unpublishText: z.string().optional(), publishAttribute: z.string().optional(), massEditConfig: MassEditConfig.optional(), updateCoordinateMap: zMassEditCoordinateMap.optional(), onlySendCoordinateMap: z.boolean().optional(), topAttributes: zConfigurableGridTopAttributes, configurablePostAction: zConfigurablePostAction.optional(), redecorateMap: z.record(z.string(), z.string()).optional(), redecorationOptions: z
        .object({
        additionalRedecorationDataIndexes: z.array(z.string()),
    })
        .optional(), configure: ConfigureViewDefn.optional(), graph: zConfigurableGridGraph.optional(), searchIndexes: z.array(z.string()).optional(), noRowsOverlay: zNoRowsOverlay.optional(), groupingKeyParams: z
        .object({
        defaultCollapsed: z.array(z.string()),
    })
        .optional() }));
export const zWorklistViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { $id: z.string().optional(), title: z.string(), defaultTab: z.string().optional(), tabs: z.array(zWorklistTab), companionView: zCompanionView, subheaderDropdowns: z.union([zSubheaderDropdowns, z.array(zSubheaderDropdowns)]).optional() }));
export const zParameterTogglesFieldBoolean = z.object({
    text: z.string(),
    dataIndex: z.string(),
    editor: z.enum(['toggle', 'multiselect', 'singleSelect', 'string', 'integer']),
    /** Text to render next to the toggle control that is always visible without hover */
    helperText: z.string().optional(),
    /** Text to render in the tooltip */
    infoText: z.string().optional(),
    dataApi: zClientDataApi.optional(),
    editable: z.boolean().optional(),
});
export const zParameterTogglesRadioField = zParameterTogglesFieldBoolean.omit({ editor: true });
export const zParameterTogglesFieldRadio = z.object({
    radioFields: z.array(zParameterTogglesRadioField),
    editor: z.enum(['radiogroup', 'radiogroupDeselectable']),
    text: z.string(),
    /** Text to render next to the radio control that is always visible without hover */
    helperText: z.string().optional(),
});
export const zParameterTogglesField = z.union([zParameterTogglesFieldBoolean, zParameterTogglesFieldRadio]);
const zParameterTogglesSection = z.object({
    title: z.string(),
    icon: z.string(),
    fields: z.array(zParameterTogglesField),
});
export const zParameterTogglesViewDefn = z.object({
    title: z.string(),
    sections: z.array(zParameterTogglesSection),
    allocPlanModel: z.string().optional(),
    fabDataApi: zListDataApi.optional(),
    subheaderDropdowns: zSubheaderDropdowns.optional(),
    dataApi: zListDataApi.optional(),
    updateCoordinateMap: zMassEditCoordinateMap.optional(),
});
export const zNestedGridViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { id: z.string(), type: z.literal('grid'), main: GridMainViewDefn, 
    // This is a legacy property used for AA Receipt Grid, but now we used the 0th entry of the componentProps.defns.view array as this value
    // This functionality should always pull from componentProps.defns.model and the model property in the viewDefn should be removed later
    // INT-2616 tracks the need for this migration
    model: z.string().optional(), view: z.array(NestedColumnsBasicViewItem.or(zBasicViewItem)) }));
export const zParetoSummaryViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { id: z.string(), view: z.array(ParetoSection) }));
export const zStoreGroupViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { view: z.array(z.object({
        text: z.string(),
        view: z.array(zBasicViewItem),
    })), main: z.object(Object.assign({ image: z.string(), title: z.string() }, zIdentifierProps.shape)) }));
export const zProductMixViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { id: z.string(), defaults: z.string(), view: z.array(NestedViewBasicViewItem).or(zBasicViewItem) }));
export const zProductivityViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { view: z.array(NestedViewBasicViewItem).or(zBasicViewItem) }));
export const zStyleCollectionViewDefn = z.object({
    /** attribute mappings for collection */
    collection: MainViewDefnConfig,
    /** todo: rename to financialsMacro */
    macros: MainViewDefnConfig,
    productivityMacros: MainViewDefnConfig,
    /** attributes to display for items  */
    items: z.array(zBasicViewItem),
});
const zCollectionAnalysisGrid = z.object({
    headerText: z.string(),
    columns: z.array(zBasicViewItem),
});
export const zCollectionAnalysisViewDefn = z.object({
    left: z.object({
        /** attribute mappings for collection */
        collection: MainViewDefnConfig,
        /** attribute mappings for collection items */
        item: MainViewDefnConfig,
    }),
    right: z.object({
        macro: MainViewDefnConfig,
        anchorGrid: zCollectionAnalysisGrid,
        clusterGrid: zCollectionAnalysisGrid,
    }),
});
export const zStyleEditViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { title: z.string(), searchIndexes: SearchIndexes, sectionsViewDefns: z.array(z.string()), companionView: zCompanionView }));
export const zStyleEditSectionsViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { header: StyleEditHeader, sections: z.array(StyleEditSection).optional().default([]) }));
export const zNestedOvertimeViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { title: z.string(), classes: z.array(z.string()).optional(), main: GridMainViewDefn.optional(), columns: z.array(zBasicViewItem), 
    // TODO: needs to match the other configure view defn shape 'ConfigureViewDefn' in general.ts
    configure: z.object({
        title: z.string(),
        defaults: z.array(z.string()),
        view: z.array(NestedOptionsBasicViewItem),
        transposedColumns: zTransposedColumns,
    }), timeLevels: TimeLevels.optional() }));
export const zNestedStyleOvertimeViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { title: z.string(), main: GridMainViewDefn, columns: z.array(zBasicViewItem), classes: z.array(z.string()).optional(), companionGrid: z
        .object({
        label: z.string(),
        sortConfig: z.object({
            defaultSelection: z.string(),
            options: z.array(zBasicViewItem),
        }),
    })
        .optional(), timeLevels: TimeLevels.optional() }));
export const zCanvasViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { main: MainViewDefnConfig, model: z.string().optional(), view: z.array(zBasicViewItem), text: z.string().optional(), assortmentModel: z.string().optional() }));
export const zSummaryViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { main: MainViewDefnConfig, view: z.array(zBasicViewItem) }));
export const zGridViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { type: z.literal('grid'), main: GridMainViewDefn, view: z.array(zBasicViewItem.or(NestedColumnsBasicViewItem)), searchIndexes: z.array(z.string()).optional() }));
export const zFlowTypeViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { searchIndexes: z.array(z.string()).optional(), main: MainViewDefnConfig, view: z.array(zBasicViewItem) }));
export const zTopTYvsLYViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { searchIndexes: z.array(z.string()).optional(), main: MainViewDefnConfig, view: z.array(zBasicViewItem) }));
export const zCollectionViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { type: z.literal('rollUp'), searchIndexes: z.array(z.string()).optional(), view: z.array(zBasicViewItem) }));
export const zFloorsetComparisonViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { main: MainView, view: z.array(zBasicViewItem) }));
export const zFlowSheetGridViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { inputType: z.literal('grid'), model: z.string(), main: GridMainViewDefn.extend({
        disableVRPHardcode: z.boolean().default(false),
    }), view: z.array(zBasicViewItem), timeLevels: TimeLevels.optional(), searchIndexes: z.array(z.string()).optional() }));
// New Mass Edit options
const OptionSchema = z.object({
    value: z.union([z.string(), z.number()]),
    label: z.string(),
});
const zExtraModifierSchema = z
    .looseObject({
    text: z.string(),
    dataIndex: z.string(),
    editor: z.enum(['radiogroup', 'checkboxgroup']), // Available editor types
    options: z.array(OptionSchema), // Options required for radio/checkbox fields
    defaultValue: z.string().or(z.number()).optional(),
})
    .passthrough();
const zModifierTypes = zBasicViewItem.extend(z.looseObject({
    editor: z.string().optional(), // to allow blank objects that used to be needed to pass through, will be ignored later
    dataApi: zDataApi.optional(),
    valueType: z.string().optional(),
    renderer: z.string().optional(),
    extraModifiers: z.array(zExtraModifierSchema).optional(),
    calculation: z.string().optional(),
}).shape);
const zMassEditViewDefn = z.union([
    z.object({
        title: z.string(),
        modifierTypes: z.array(zModifierTypes).optional(),
        dataIndex: z.string().optional(),
    }),
    z.object({
        dataIndex: z.string(),
        editor: z.string(),
        title: z.string(),
    }),
]);
export const zMassEditDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { hideEmpty: z.boolean().optional(), views: z.array(zMassEditViewDefn) }));
export const zMacroMixDropdowns = z.object({
    xtype: z.literal('heatmap-selections'),
    text: z.string().default('Select Metric:'),
    type: z.literal('dropdown'),
    view: z.array(z.object({
        text: z.string(),
        modelId: z.string(),
        view: z.array(z.object({
            dataIndex: z.string(),
            dimension: z.string(),
        })),
    })),
});
export const zMacroMixHeatMap = z.object({
    xtype: z.literal('heatmap-conf'),
    dataIndex: z.string(),
    view: z.array(z.object({
        xtype: z.union([z.literal('display'), z.literal('trend')]),
        dataIndex: z.string(),
    })),
});
export const zMacroMixLineGraph = z.object({
    text: z.string().default('Select Metric:'),
    chartType: z.enum(['line', 'pie']).optional(),
    view: z.array(z.object(Object.assign(Object.assign({}, zChartTooltipConfig.shape), { 
        /** New property added in INT-4498 to support different x-axis category labels in the chart side charts in MacroMix */
        title: z.string().optional(), sortIndex: z.string().optional(), view: z.array(z.object({
            dataIndex: z.string(),
            text: z.string(),
            bubble: zChartTooltipConfig,
        })) }))),
});
// these are split because the discriminated union doesn't support spliting on union, such as 'left'.or('right')
export const zMacroMixLineGraphLeft = z.object(Object.assign(Object.assign({}, zMacroMixLineGraph.shape), { xtype: z.literal('left-chart') }));
export const zMacroMixLineGraphRight = z.object(Object.assign(Object.assign({}, zMacroMixLineGraph.shape), { xtype: z.literal('right-chart') }));
export const zMacroMixHeatMapToolTip = z.object({
    xtype: z.literal('description'),
    renderer: z.string(),
    mask: z.string(),
    dataIndex: z.string(),
});
export const zMacroMixViewDefn = z.object({
    type: z.literal('screen'),
    view: z.array(z.discriminatedUnion('xtype', [
        zMacroMixDropdowns,
        zMacroMixHeatMap,
        zMacroMixLineGraphLeft,
        zMacroMixLineGraphRight,
        zMacroMixHeatMapToolTip,
    ])),
});
export const zSankeySideChartView = z.object({
    text: z.string(),
    dataIndex: z.string(),
    renderer: z.string().optional(),
});
export const zSankeyGroupedViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { id: z.string().optional(), title: z.string().optional(), groupByOptions: z
        .array(z.union([
        z.string(),
        z.array(z.string()),
        z.object({
            text: z.string(),
            dataIndex: z.union([z.string(), z.array(z.string())]),
        }),
    ]))
        .optional(), metricIndex: z.string().optional(), defaults: z
        .object({
        grouping: z.array(z.string()).optional(),
    })
        .optional(), view: z.array(z.any()).optional() }));
export const zSizeEligibilityListGridViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { id: z.string(), type: z.string(), main: z.object({
        title: z.string(),
    }), companionApi: zCompanionViewDefn, update: z.object({
        type: z.string(),
        keys: z.object({
            product: z.string(),
            cluster: z.string(),
        }),
    }), columns: z.array(zBasicViewItem) }));
export const zPublishAllocationGridViewDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { dataApi: z.object({
        isListData: z.boolean(),
        defnId: z.string(),
        params: z.object({
            aggBy: z.string(),
        }),
    }), main: GridMainViewDefn, title: z.string(), view: z.array(zBasicViewItem) }));
export const zGeoTrendsMapDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { defaultAggBy: z.string(), default: z.string(), mapUri: z.string(), title: z.string().optional(), bubble: zChartTooltipConfig, map: zBasicViewItem.extend(z.object({
        minSize: z.string().or(z.number()),
        maxSize: z.string().or(z.number()),
        type: z.string(),
        trendDataIndex: z.string(),
        zoomBy: z
            .object({
            howMuch: z.number().or(z.undefined()),
            coords: z
                .object({
                lat: z.number(),
                lon: z.number(),
            })
                .optional(),
        })
            .optional(),
    }).shape), view: z.array(zBasicViewItem.extend(z.object({
        groupingKey: z.string(),
        geoLocationKeys: z.array(z.string()),
    }).shape)), trends: z.array(z.object({
        text: z.string(),
        color: z.string(),
        trendValueMap: z.string().or(z.number()),
    })) }));
export const zGeoTrendsChartsDefn = z.object(Object.assign(Object.assign({}, zHasAdornments.shape), { view: z.array(zBasicViewItem.extend(z.object({
        chartType: z.enum(['line', 'pie']).optional(),
        sortIndex: z.string().optional(),
        view: z.array(zBasicViewItem.extend(z.object({
            bubble: zChartTooltipConfig,
        }).shape)),
    }).shape)) }));
export const zTrendCardType = z.enum(['metric', 'prediction']);
export const zVariancePillConfig = z.object({
    valueDataIndex: z.string(),
    varianceDataIndex: z.string().or(z.null()),
    text: z.string(),
    valueRenderer: z.string().optional().default('usMoneyAbbreviated'),
    varianceRenderer: z.string().optional().default('simplePercent'),
});
const zTrendSummaryCardConfig = z.object({
    trendCardType: zTrendCardType,
    title: z.string(),
    trendDataIndex: z.string(),
    trendRenderer: z.string(),
    trendSummary: z.string(),
    currentDataIndex: z.string(),
    currentRenderer: z.string(),
    pills: z.array(zVariancePillConfig),
});
const zRendererParamsBar = z
    .object({
    tooltip: z.object({
        header: z.string(),
        value: z.string(),
        target: z.string(),
        dataSuffix: z.string(),
        renderer: z.string(),
    }),
    bars: z.array(z.object({
        valueDataIndex: z.string(),
        targetDataIndex: z.string(),
    })),
})
    .optional();
export const zTrendDetailsGridColumns = z.array(z.object({
    dataIndex: z.string(),
    text: z.string(),
    renderer: z.string().optional(),
    xtype: z.string().optional(),
    width: z.number().optional(),
    rendererParams: z
        .object({
        pills: z.array(zVariancePillConfig).optional(),
        bar: zRendererParamsBar,
        stacks: z.array(z.object({ dataIndex: z.string() })).optional(),
        subRenderer: z.string().optional(),
    })
        .optional(),
}));
export const zTrendDetailsGridConfig = z.object(Object.assign(Object.assign({ type: z.string().optional() }, zHasTitle('Trend Details').shape), { grid: z.object({
        main: GridMainViewDefn.optional(),
        columns: zTrendDetailsGridColumns,
    }) }));
export const zTrendSummaryConfig = z.object(Object.assign(Object.assign({ type: z.literal('trendCards').optional().default('trendCards'), cards: z.array(zTrendSummaryCardConfig) }, zHasTitle('Trend Summary').shape), zhasCardWidth.shape));
const zTrendBubblesConfig = z.object(Object.assign(Object.assign(Object.assign({}, zHasTitle('').shape), { type: z.literal('trendBubbles').optional().default('trendBubbles'), graph: z.object({
        dataIndex: z.string(),
        groupDataIndex: z.string(),
        nameDataIndex: z.string(),
    }), chartOptions: z.object({
        chart: z.object({
            type: z.literal('packedbubble'),
        }),
        // taken from geotrends to match the existing bubble config we already have,
        // otherwise this is a straight copy of the highcharts config
        bubble: zChartTooltipConfig,
        // `plotOptions` here is intended to be close to a 1:1 replica of the highcharts `packedbubble` chart type
        plotOptions: z.object({
            packedbubble: z.object({
                minSize: z.string().optional(),
                maxSize: z.string().optional(),
                zMin: z.number().optional(),
                zMax: z.number().optional(),
                layoutAlgorithm: z.object({
                    gravitationalConstant: z.number().optional(),
                    splitSeries: z.boolean().optional(),
                    seriesInteraction: z.boolean().optional(),
                    dragBetweenSeries: z.boolean().optional(),
                    parentNodeLimit: z.boolean().optional(),
                }),
                dataLabels: z.object({
                    enabled: z.boolean().optional().default(true),
                    format: zChartTooltipConfig,
                    filter: z.object({
                        property: z.string().optional(),
                        operator: z.string().optional(),
                        value: z.number().optional(),
                    }),
                    style: z.object({
                        color: z.string().optional(),
                        textOutline: z.string().optional(),
                        fontWeight: z.string().optional(),
                    }),
                }),
            }),
        }),
    }) }), zhasCardWidth.shape));
export const zTrendConfig = z.union([zTrendSummaryConfig, zTrendBubblesConfig, zTrendDetailsGridConfig]);
const zAgentStepInput = z
    .object({
    type: z.literal('agentInput'),
    text: z.string(),
    description: z.string().optional(),
    configApi: zDataApi.optional(),
    dataApi: zDataApi.optional(),
    inputType: z.string(),
})
    .describe('Agent Action Selection Step, such as fetching from Valid Values');
const zAgentStepPreview = z
    .object({
    type: z.literal('agentPreview'),
    text: z.string(),
    description: z.string().optional(),
    dataApi: zDataApi.optional(),
    view: z.array(zTopAttribute),
})
    .describe('Agent Action Data Preview rendering a data about the action');
const zAgentAction = z.object({
    text: z.string(),
    description: z.string(),
    icon: z.string(),
    steps: z.array(z.union([zAgentStepPreview, zAgentStepInput, zConfigurableGridLinkButton])),
});
const zAgentActionGroup = z.object({
    text: z.string(),
    description: z.string(),
    actions: z.array(zAgentAction),
});
export const zAgentConfig = z
    .object({
    actions: z.object({
        view: z.array(zAgentActionGroup),
    }),
    freeform: z.object({
        enabled: z.boolean().default(true).optional(),
    }),
})
    .describe('Agent Panel View Config');
export const zStylePane = z.looseObject({
    segment: z.string(),
    model: z.string(),
    updateCoordinateMap: zMassEditCoordinateMap.optional(),
    view: z.array(z.looseObject({
        text: z.string().optional(),
        dataIndex: z.string().optional(),
        style: z.string().optional(),
        editable: z.boolean().optional().default(false),
        classes: z.array(z.string()).optional(),
        xtype: z.enum(['swatches', 'validoptions', 'image', 'array', 'string']).optional(),
        /** pins this view entry to the top of the pane so it stays visible while the rest scrolls */
        pinned: z.enum(['top']).optional(),
        /** this is loose because all of the style edit params can pass through here,
         * and I haven't enumerated them all
         * TODO: unify this type with Style Edit DividedColumnDetails columns object
         */
        columns: z
            .array(z.looseObject({
            text: z.string(),
            dataIndex: z.string(),
            renderer: z.string().optional(),
            editable: z.boolean().optional().default(false),
            classes: z.array(z.string()).optional(),
        }))
            .optional(),
    })),
});
